import React, { useEffect, useState } from 'react';
import {
  LogOut,
  Globe,
  Shield,
  Pencil,
  Check,
  X,
  Loader2,
  KeyRound,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { LanguageDropdown } from '../layout/Layout';
import TierCrest from '../components/stamps/TierCrest';
import { getRank } from '../utils/tiers';
import { validatePassword, isPasswordValid } from '../utils/password';
import { useLocalizedNavigate, useLang } from '../i18n/useLocalizedNav';

type StampData = { multiplier: number | null };

const Profile: React.FC = () => {
  const { user, updateUser, logout } = useAuth();
  const { t } = useTranslation();
  const lang = useLang();
  const navigate = useLocalizedNavigate();

  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const [stamps, setStamps] = useState<StampData[]>([]);
  const [memberSince, setMemberSince] = useState<string | null>(null);

  // Name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user?.fullName || '');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');

  // Password change
  const [showPwForm, setShowPwForm] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwOk, setPwOk] = useState(false);

  useEffect(() => {
    (async () => {
      const [mine, exportData] = await Promise.all([
        api.get('/stamps/my'),
        api.get('/auth/account/export'),
      ]);
      if (Array.isArray(mine)) setStamps(mine);
      if (exportData?.profile) {
        setAuthProvider(exportData.profile.authProvider ?? null);
        setMemberSince(exportData.profile.createdAt ?? null);
      }
    })();
  }, []);

  if (!user) return null;

  const earned = stamps.filter((s) => s.multiplier !== null);
  const collected = earned.reduce((sum, s) => sum + (s.multiplier ?? 1), 0);
  const bestMultiplier = earned.reduce(
    (max, s) => Math.max(max, s.multiplier ?? 0),
    0,
  );
  const rank = getRank(earned.length);

  const isGoogle = authProvider === 'google';
  const initials = (user.fullName || user.email)
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const saveName = async () => {
    setSavingName(true);
    setNameError('');
    const res = await api.patch('/auth/account', { fullName: nameValue });
    setSavingName(false);
    if (res?.error) {
      setNameError(res.error);
      return;
    }
    updateUser({ fullName: res.profile?.fullName ?? nameValue.trim() });
    setEditingName(false);
  };

  const changePassword = async () => {
    setSavingPw(true);
    setPwError('');
    setPwOk(false);
    const res = await api.post('/auth/change-password', {
      currentPassword: currentPw,
      newPassword: newPw,
    });
    setSavingPw(false);
    if (res?.error) {
      setPwError(res.error);
      return;
    }
    setPwOk(true);
    setCurrentPw('');
    setNewPw('');
    setShowPwForm(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <h1 className="font-display text-4xl text-warm-800">{t('profile_title')}</h1>

      {/* Identity + passport snapshot */}
      <div className="bg-brand-secondary text-white rounded-3xl p-8 space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-brand-primary rounded-full flex items-center justify-center font-heading font-bold text-xl text-brand-secondary shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            {editingName ? (
              <div className="space-y-2">
                <input
                  autoFocus
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 text-white outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder={t('profile_name_placeholder')}
                />
                {nameError && (
                  <p className="text-brand-danger text-xs font-bold">
                    {nameError}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={saveName}
                    disabled={savingName}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary text-brand-secondary text-sm font-heading font-bold rounded-lg disabled:opacity-50"
                  >
                    {savingName ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    {t('profile_save')}
                  </button>
                  <button
                    onClick={() => {
                      setEditingName(false);
                      setNameValue(user.fullName || '');
                      setNameError('');
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white text-sm font-heading font-bold rounded-lg"
                  >
                    <X className="w-3.5 h-3.5" />
                    {t('profile_cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-2xl text-white truncate">
                    {user.fullName || t('profile_adventurer')}
                  </h2>
                  <button
                    onClick={() => {
                      setNameValue(user.fullName || '');
                      setEditingName(true);
                    }}
                    className="text-warm-500 hover:text-brand-primary transition-colors"
                    aria-label={t('profile_edit_name')}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-warm-500 text-sm truncate">{user.email}</p>
              </>
            )}
          </div>
        </div>

        {/* Passport snapshot */}
        <div className="flex items-center gap-5 bg-white/5 rounded-2xl p-5">
          <TierCrest
            color={rank.tier.crest}
            label={rank.tier.short}
            className="w-14 h-16 shrink-0"
          />
          <div className="flex-1 space-y-2">
            <p className="font-display text-xl text-brand-primary">
              {t('ms_rank', { tier: t(`tier_${rank.tier.short}`) })}
            </p>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-primary rounded-full"
                style={{ width: `${rank.progress * 100}%` }}
              />
            </div>
            <p className="text-warm-500 text-xs">
              {rank.nextTier
                ? t('ms_until_next', {
                    count: rank.toNext,
                    tier: t(`tier_${rank.nextTier.short}`),
                  })
                : t('ms_top_reached')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { value: collected, label: t('ms_stat_collected') },
            { value: earned.length, label: t('ms_stat_unique') },
            { value: `×${bestMultiplier}`, label: t('ms_stat_best') },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 rounded-2xl p-4">
              <p className="font-display text-2xl text-white">{s.value}</p>
              <p className="font-heading font-bold text-[10px] tracking-[0.12em] text-warm-500 uppercase mt-1">
                {s.label}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="px-3 py-1.5 bg-white/5 rounded-lg text-warm-500 font-heading font-bold uppercase tracking-widest flex items-center gap-1.5">
            <Shield className="w-3 h-3" />
            {t('profile_verified_member')}
          </span>
          {memberSince && (
            <span className="px-3 py-1.5 bg-white/5 rounded-lg text-warm-500 font-heading font-bold uppercase tracking-widest">
              {t('profile_since', {
                date: new Date(memberSince).toLocaleDateString(lang, {
                  month: 'short',
                  year: 'numeric',
                }),
              })}
            </span>
          )}
        </div>
      </div>

      {/* Settings */}
      <div className="bg-white rounded-3xl border border-warm-200 shadow-sm divide-y divide-warm-200">
        {/* Password */}
        {!isGoogle && (
          <div className="p-5 space-y-4">
            <button
              onClick={() => {
                setShowPwForm((v) => !v);
                setPwError('');
                setPwOk(false);
              }}
              className="w-full flex items-center justify-between"
            >
              <span className="flex items-center gap-3">
                <KeyRound className="w-5 h-5 text-warm-500" />
                <span className="font-bold text-warm-800">{t('profile_change_password')}</span>
              </span>
              <span className="text-sm text-warm-500">
                {showPwForm ? t('profile_close') : t('profile_edit')}
              </span>
            </button>
            {pwOk && (
              <p className="text-brand-success text-sm font-bold flex items-center gap-1.5">
                <Check className="w-4 h-4" />
                {t('profile_password_updated')}
              </p>
            )}
            {showPwForm && (
              <div className="space-y-3">
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    placeholder={t('profile_current_password')}
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    className="w-full pl-4 pr-12 py-3 bg-warm-100 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    aria-label={showPw ? t('auth_hide_password') : t('auth_show_password')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-warm-500 hover:text-brand-accent transition-colors"
                  >
                    {showPw ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    placeholder={t('profile_new_password')}
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    className="w-full pl-4 pr-12 py-3 bg-warm-100 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    aria-label={showPw ? t('auth_hide_password') : t('auth_show_password')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-warm-500 hover:text-brand-accent transition-colors"
                  >
                    {showPw ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {validatePassword(newPw).map((rule) => (
                    <div
                      key={rule.id}
                      className={`flex items-center gap-2 text-[10px] font-bold uppercase transition-colors ${
                        rule.met ? 'text-brand-success' : 'text-warm-500'
                      }`}
                    >
                      <div
                        className={`w-1 h-1 rounded-full ${
                          rule.met ? 'bg-brand-success' : 'bg-warm-200'
                        }`}
                      />
                      <span>{t(`password_rule_${rule.id}`)}</span>
                    </div>
                  ))}
                </div>
                {pwError && (
                  <p className="text-brand-danger text-sm font-bold">
                    {pwError}
                  </p>
                )}
                <button
                  onClick={changePassword}
                  disabled={
                    savingPw || !currentPw || !isPasswordValid(newPw)
                  }
                  className="inline-flex items-center gap-2 px-5 py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl hover:bg-brand-accent transition-all disabled:opacity-50"
                >
                  {savingPw ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('profile_update_password')
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Language */}
        <div className="w-full p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-warm-500" />
            <span className="font-bold text-warm-800">{t('profile_language')}</span>
          </div>
          <LanguageDropdown />
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full p-5 flex items-center gap-3 hover:bg-brand-danger/10 transition-colors"
        >
          <LogOut className="w-5 h-5 text-brand-danger" />
          <span className="font-bold text-brand-danger">{t('profile_logout')}</span>
        </button>
      </div>

      <p className="text-center text-warm-500 text-sm">
        {t('profile_manage_data_1')}{' '}
        <button
          onClick={() => navigate('/privacy')}
          className="font-bold text-brand-accent hover:underline"
        >
          {t('profile_privacy_policy')}
        </button>
        .
      </p>
    </div>
  );
};

export default Profile;
